import type { Kasten } from '@schnitt/shared';
import { ECKEN_RADIUS, LUT_GROESSE, MASKE_ELLIPSEN_MAX, STUFEN_JE_EINHEIT, chromaUV8, ebenenEcken, ellipsen, lutWuerfel, rgbVon, vignetteRahmen } from '@schnitt/shared';
import type { Maskenform } from '@schnitt/shared';
import { grafikkarteGewuenscht } from './leinwandwahl';

/**
 * Die Leinwand malt selbst (7.69a).
 *
 * Bisher lagen auf der Bühne bis zu sieben Elemente übereinander -- zwei
 * Videoflächen, eine je Ebene, dazu Text und Bauchbinde --, jedes mit eigener
 * Deckung und eigenem Filter, und jedes eine eigene Fläche im Compositor. Auf
 * einem Telefon ist das die teure Art, ein Bild zu zeigen (Sascha,
 * 8. September: „sobald … ein anderes Video drin, stottert es wieder total“).
 *
 * Hier steht die andere Art: ein `<canvas>`, in das die Bilder gezeichnet
 * werden. Ein Aufruf je Ebene, alles auf der Grafikkarte, ein Bild am Ende.
 *
 * Warum WebGL2 und nicht WebGPU, wo Sascha doch WebGPU gesagt hat: weil es
 * dieselbe Sache ist, nur die Anrede ist eine andere. WebGL2 gibt es auf
 * jedem iPhone seit Jahren, WebGPU erst ab iOS 26. Der `Maler` ist die
 * Schnittstelle; ein zweiter Maler auf WebGPU wird später *dahinter*
 * geschoben, nicht danebengebaut. Wer heute umbaut, hat morgen die Wahl.
 *
 * Die Lage der Ebenen rechnet `leinwand.ts` im gemeinsamen Teil aus -- damit
 * Vorschau und Export nicht auseinanderlaufen. Hier steht nur das Malen.
 */

/**
 * Ob die Leinwand in dieser Sitzung selbst malt.
 *
 * Einmal beim Laden entschieden (siehe `leinwandwahl.ts`): aus der Wahl in
 * der Verwaltung oder aus `?gl=1` an der Adresse. Eine Konstante, keine
 * Funktion, weil die Vorschau sich auf den einen oder den anderen Weg
 * aufbaut und nicht mitten im Schnitt umschwenkt.
 */
export const GL_GEWUENSCHT = grafikkarteGewuenscht();

/**
 * Was die Leinwand tut, gezählt (7.69h).
 *
 * Sascha, 8. September, vom Gerät, nach der ersten Reparatur: „Hmm leider
 * keine flüssigen Videos.“ Wieder raten hilft nicht -- hier stehen die
 * Zahlen, die die Frage entscheiden: wie oft gezeichnet wird, wie oft ein
 * Video hochgeladen wird, wie lange ein Upload dauert und auf welchem Weg.
 * Das Melde-Blatt schickt sie mit; `window.__leinwandBericht()` liest sie.
 */
export const messung = {
  gezeichnet: 0,
  hochgeladen: 0,
  ms: 0,
  msMax: 0,
  /** Welcher Weg gerade benutzt wird, und was die Probe je Weg ergab. */
  weg: 'sub' as 'sub' | 'neu',
  probe: { sub: { n: 0, ms: 0 }, neu: { n: 0, ms: 0 } },
  seit: typeof performance !== 'undefined' ? performance.now() : 0,
};

export function leinwandBericht(): {
  sekunden: number;
  gezeichnetJeSekunde: number;
  hochgeladenJeSekunde: number;
  msJeUpload: number;
  msMax: number;
  weg: string;
  probe: { sub: number; neu: number };
} {
  const sekunden = Math.max(0.001, (performance.now() - messung.seit) / 1000);
  const r = (v: number) => Math.round(v * 10) / 10;
  return {
    sekunden: r(sekunden),
    gezeichnetJeSekunde: r(messung.gezeichnet / sekunden),
    hochgeladenJeSekunde: r(messung.hochgeladen / sekunden),
    msJeUpload: r(messung.ms / Math.max(1, messung.hochgeladen)),
    msMax: r(messung.msMax),
    weg: messung.weg === 'sub' ? 'texSubImage2D' : 'texImage2D',
    probe: {
      sub: r(messung.probe.sub.ms / Math.max(1, messung.probe.sub.n)),
      neu: r(messung.probe.neu.ms / Math.max(1, messung.probe.neu.n)),
    },
  };
}

export function messungZurueck(): void {
  messung.gezeichnet = 0;
  messung.hochgeladen = 0;
  messung.ms = 0;
  messung.msMax = 0;
  messung.seit = performance.now();
}

/** Was gezeichnet werden soll. Entweder ein Bild oder eine Farbe. */
export interface Ebene {
  /** Ein Video, ein Bild, ein Canvas -- alles, was die Grafikkarte annimmt. */
  quelle: TexImageSource | null;
  /** Wenn keine Quelle da ist: eine Farbe, 0..1 je Kanal. */
  farbe?: [number, number, number];
  kasten: Kasten;
  x: number;
  y: number;
  scale: number;
  rotate: number;
  deckung: number;
  /**
   * Wie `cssFilter`: Helligkeit, Kontrast, Sättigung, Gamma. 1/1/1/1 heisst
   * „wie aufgenommen“. Das Gamma kam mit 7.133 dazu -- vorher rechnete die
   * Vorschau es auf keinem der beiden Wege, während der Film es anwandte.
   */
  werte?: [number, number, number, number];
  /** Der Look (7.69c): Name aus `LOOKS` und wie stark, 0..1. */
  look?: { name: string; staerke: number };
  /** Die Farbräder (S14): je Bereich drei Zahlen -1..1 für Rot, Grün, Blau. */
  raeder?: { tiefen: readonly number[]; mitten: readonly number[]; hoehen: readonly number[] };
  /**
   * Der Greenscreen (S11, 7.144): die Schlüsselfarbe als `#rrggbb`, wie viel
   * Abstand noch wegfällt und wie weich die Kante ist. Gerechnet wird, was
   * `chromakey` rechnet -- die Formel steht im gemeinsamen Teil.
   */
  chroma?: { color: string; toleranz: number; weich: number };
  /**
   * **Der laufende Übergang** (7.147): welche Form, wie weit, und wie gross
   * die Leinwand ist. Die Maske steht in Leinwandkoordinaten und nicht in
   * denen der Ebene -- `xfade` schiebt seine Kante durchs ganze Bild.
   */
  uebergang?: { art: number; p: number; raster?: number };
  /**
   * Wie die Ebene auf dem liegt, was schon gemalt ist (7.132): 0 normal,
   * 1 multiply, 2 screen, 3 softlight. Dieselben vier, die die Spur kennt.
   */
  mischart?: number;
  /** Die Vignette, 0..1 -- 0 heisst keine. */
  vignette?: number;
  /**
   * Weichzeichnen, Sigma in **Punkten des Films** -- wie `gblur=sigma=` im
   * Export, der die Quelle erst ins Bild einpasst und dann weich zeichnet.
   * `zeichne` rechnet es auf die Punkte der Quelle um (7.173); bis zum 25.
   * September galt es direkt dort, und eine Vorschau-Datei ist höchstens 720
   * breit -- bei einer Aufnahme aus dem Telefon im 1080er Projekt war die
   * Vorschau anderthalbmal zu weich. 0 heisst keins.
   */
  blur?: number;
  /**
   * Die Form, in der die Ebene läuft (7.82) -- Kreis, Ellipse, runde Ecken,
   * Gedankenblase, samt Lage und weicher Kante.
   *
   * **Bis zum 22. September kannte der Maler sie gar nicht** (7.138): der
   * Export schnitt die Form, die Vorschau zeigte das ganze Rechteck.
   * Gerechnet wird hier dieselbe Geometrie wie in `drin()` im gemeinsamen
   * Teil -- eine zweite Formel wäre eine zweite Wahrheit.
   */
  maske?: { form: Maskenform; weich: number; x: number; y: number; groesse: number; drehung: number; kontur?: { farbe: string; staerke: number } | null; gespiegelt?: boolean };
  /**
   * Stand der Quelle. Steht hier eine Zahl, wird die Textur nur neu
   * hochgeladen, wenn sie sich ändert -- für Bilder und gesetzten Text, die
   * sich nicht bei jedem Bild ändern. Ohne Zahl (ein Video) jedes Mal.
   */
  version?: number;
}

const VERTEX = `#version 300 es
in vec2 platz;
in vec2 stelle;
out vec2 vStelle;
void main() {
  vStelle = stelle;
  gl_Position = vec4(platz, 0.0, 1.0);
}`;

/**
 * Helligkeit, Kontrast und Sättigung rechnen hier genau so wie die
 * CSS-Filter, die sie bisher gemacht haben -- und wie ffmpeg beim Export.
 * Eine Vorschau, die anders rechnet, ist eine Vorschau, die lügt.
 */
const FRAGMENT = `#version 300 es
precision mediump float;
precision mediump sampler3D;
in vec2 vStelle;
uniform sampler2D bild;
uniform sampler3D look;
uniform float lookStaerke;
uniform float lookSchritt;
uniform float vignette;
uniform float seiten;
uniform vec2 vigSpanne;
uniform float deckung;
uniform int istFarbe;
uniform vec3 farbe;
uniform vec3 werte;
uniform float gammaWert;
uniform sampler2D hinten;
uniform int mischart;
uniform vec3 radTiefen;
uniform vec3 radMitten;
uniform vec3 radHoehen;
uniform float radSkala;
// **Der Greenscreen** (S11, seit 7.144 auch hier). chromaAn: 0 aus, 1 an.
// chromaUV ist die Schluesselfarbe in U und V, beide um die Mitte herum.
uniform float chromaAn;
// Volle Genauigkeit, und das ist kein Schmuck: gerundet wird bei 128, und
// mediump traegt dort nur Viertelschritte -- eine Rundung, die um eins
// danebengeht, verschiebt die weiche Kante um Prozente.
uniform highp vec2 chromaUV;
uniform highp float chromaTol;
uniform highp float chromaWeich;
// **Der Übergang als Form** (7.147/7.148). 0 keiner, sonst die Stelle in
// UEBERGAENGE_MIT_FORM um eins nach oben: 1 wipeleft, 2 wiperight, 3 wipeup,
// 4 wipedown, 5 horzopen, 6 vertopen, 7 circleopen, 8 circleclose,
// 9 diagtl, 10 diagbr, 11 radial.
uniform int uebergangArt;
uniform float uebergangP;
uniform vec2 leinwandGroesse;
// **Das Raster** (pixelize, 7.148). Keine Form, sondern eine andere
// Abtaststelle: die Seite eines Feldes als Anteil der kuerzeren Bildseite.
// Null heisst „kein Raster“.
uniform float uebergangRaster;
// **Die Form, in der die Ebene laeuft** (7.82/7.138). maskeArt: 0 keine,
// 1 eine Vereinigung von Ellipsen, 2 runde Ecken. Gerechnet wird dieselbe
// Geometrie wie in drin() im gemeinsamen Teil.
uniform int maskeArt;
uniform int maskeAnzahl;
uniform vec4 maskeE[${MASKE_ELLIPSEN_MAX}];
uniform float maskeWeich;
uniform vec4 maskeLage;
uniform float maskeSeiten;
// Die Kontur (7.224): so weit nach innen beginnt das Bild, davor steht
// maskeKontur. 0 heisst: keine Kontur.
uniform float maskeInnen;
uniform vec3 maskeKontur;
out vec4 raus;

// Die weiche Kante, wie kante() im gemeinsamen Teil: tiefe ist der Abstand
// nach innen, in Anteilen der Ebene.
float maskenKante(float tiefe, float weich) {
  if (weich <= 0.0) return tiefe >= 0.0 ? 1.0 : 0.0;
  return clamp(tiefe / weich + 0.5, 0.0, 1.0);
}

/**
 * Wie deckend ein Punkt der Ebene ist -- 1 drin, 0 draussen.
 *
 * Nur das *Seitenverhaeltnis* der Ebene geht ein und nicht ihre Groesse: in
 * insFeld() und drin() kuerzt sich die Groesse weg, weil jeder Abstand mit
 * ihr multipliziert und danach durch sie geteilt wird. Also breite =
 * maskeSeiten und hoehe = 1.
 */
float maskeDeckung(vec2 st, float innen) {
  if (maskeArt == 0) return 1.0;
  float b = maskeSeiten;
  float h = 1.0;
  float g = max(1e-6, maskeLage.z);
  float c = cos(maskeLage.w);
  float si = sin(maskeLage.w);
  float dx = (st.x - maskeLage.x) * b;
  float dy = (st.y - maskeLage.y) * h;
  vec2 f = vec2((dx * c - dy * si) / g / b + 0.5, (dx * si + dy * c) / g / h + 0.5);
  float w = clamp(maskeWeich, 0.0, 0.5);
  if (maskeArt == 2) {
    float k = min(b, h);
    float r = ${ECKEN_RADIUS.toFixed(6)} * k;
    float qx = abs(f.x - 0.5) * b - (b * 0.5 - r);
    float qy = abs(f.y - 0.5) * h - (h * 0.5 - r);
    float abstand = length(vec2(max(qx, 0.0), max(qy, 0.0))) - r;
    return maskenKante(-abstand / k - innen, w);
  }
  float beste = 0.0;
  for (int i = 0; i < ${MASKE_ELLIPSEN_MAX}; i++) {
    if (i >= maskeAnzahl) break;
    vec4 e = maskeE[i];
    float d = length(vec2((f.x - e.x) / e.z, (f.y - e.y) / e.w));
    beste = max(beste, maskenKante((1.0 - d) * min(e.z, e.w) - innen, w));
  }
  return beste;
}
void main() {
  // **Die Abtaststelle, und erst danach alles andere** (pixelize, 7.148).
  //
  // Die sechzehn Arten davor sagen, *wie viel* vom neuen Clip an dieser
  // Stelle steht. Das Raster sagt etwas anderes: *woher* der Punkt kommt.
  // Beide Bilder werden an derselben, groeber gerasterten Stelle abgelesen,
  // und dazwischen wird ganz gewoehnlich ueberblendet.
  //
  // Gerastert wird in **Leinwandpunkten** und nicht in der Ebene, denn so
  // rechnet ffmpeg: es rastert das fertige Bild. Der Weg von dort zurueck in
  // die Textur ist die Ableitung der Abtaststelle nach den Fensterpunkten --
  // die Abbildung ist affin, also ist sie ueber die ganze Flaeche dieselbe.
  // dFdx und dFdy stehen hier oben und ausserhalb jeder Verzweigung, weil sie
  // sonst undefiniert waeren.
  vec2 dSdx = dFdx(vStelle);
  vec2 dSdy = dFdy(vStelle);
  vec2 abtast = vStelle;
  if (uebergangRaster > 0.0) {
    float seite = uebergangRaster * min(leinwandGroesse.x, leinwandGroesse.y);
    // gl_FragCoord liegt in der Mitte des Punktes und zaehlt y von unten;
    // ffmpeg zaehlt von oben und ab null.
    float xp = gl_FragCoord.x - 0.5;
    float yp = leinwandGroesse.y - gl_FragCoord.y - 0.5;
    float xq = floor((floor(xp / seite) + 0.5) * seite);
    float yq = floor((floor(yp / seite) + 0.5) * seite);
    abtast = vStelle + dSdx * (xq - xp) - dSdy * (yq - yp);
  }
  vec4 quelle = istFarbe == 1 ? vec4(farbe, 1.0) : texture(bild, abtast);
  // Die Durchsichtigkeit der Quelle zaehlt mit (7.129). Ein gesetzter Text ist
  // fast ueberall durchsichtig; wurde nur rgb genommen, kam die Ebene als
  // deckendes Rechteck heraus -- schwarz, wo nichts steht -- und deckte alles
  // darunter ab.
  //
  // **Zurueck auf gerade Werte** (7.135). Die Textur kommt vormultipliziert
  // herein (das braucht das Weichzeichnen), die Farbrechnungen darunter
  // stehen aber auf geraden Werten -- so rechnen das Stilblatt und der Film.
  // Gemessen am 21. September: ein Textkasten mit boxOpacity 0,6 auf
  // rgb(30,122,60) ergab rgb(45,134,227) statt rgb(32,100,146).
  vec3 c = quelle.a > 0.001 ? quelle.rgb / quelle.a : quelle.rgb;
  // Die Farbe, wie sie hereinkommt -- an ihr haengt der Greenscreen (7.144).
  // Geschluesselt wird das Tuch und nicht das Ergebnis einer Farbkorrektur:
  // eine Vignette dunkelt den Rand ab, und ein Schluessel, der danach
  // rechnet, liesse das Tuch dort stehen.
  vec3 roh = c;
  // Abgeschnitten wird nach *jedem* Schritt, nicht erst am Ende (7.130). So
  // rechnet das Stilblatt: eine Kette von filter()-Funktionen ist eine Kette
  // von Schritten, und jeder gibt Werte zwischen 0 und 1 heraus. Wer erst am
  // Ende abschneidet, traegt einen Wert von 1,14 in die Saettigung hinein --
  // am 21. September gemessen: auf der Grafikkarte kam rgb(148, 80, 217)
  // heraus, im Dokument rgb(147, 78, 194).
  c = clamp(c * werte.x, 0.0, 1.0);
  c = clamp((c - 0.5) * werte.y + 0.5, 0.0, 1.0);
  // Das Gamma gehoert zu den Tonwerten (7.133): je Kanal pow(v, 1/g), nach
  // dem Kontrast und vor der Saettigung -- dieselbe Reihenfolge wie in
  // farbwerteAuf, im Stilblatt und in der Kennlinie des Films.
  if (gammaWert != 1.0) c = clamp(pow(c, vec3(1.0 / gammaWert)), 0.0, 1.0);
  float grau = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = clamp(mix(vec3(grau), c, werte.z), 0.0, 1.0);
  if (radSkala > 0.0) {
    // Die Farbraeder (S14), dieselbe Rechnung wie farbrampe im Browser:
    // drei Gewichte, die an jeder Stelle zusammen 1 ergeben. Die Kennlinie
    // dort hat ihren Knick bei 0,5 und ist stueckweise gerade -- damit
    // treffen Tabelle und Shader denselben Wert, nicht ungefaehr denselben.
    vec3 unten = max(vec3(0.0), 1.0 - 2.0 * c);
    vec3 mitte = 1.0 - abs(2.0 * c - 1.0);
    vec3 oben = max(vec3(0.0), 2.0 * c - 1.0);
    c = clamp(c + (unten * radTiefen + mitte * radMitten + oben * radHoehen) * radSkala, 0.0, 1.0);
  }
  if (lookStaerke > 0.0) {
    // Der Wuerfel hat N Stuetzstellen; die erste liegt in der Mitte des
    // ersten Texels. Ohne diesen halben Schritt zieht die Grafikkarte am
    // Rand vom Nachbarn her, und Schwarz wird ein Hauch heller.
    vec3 stelle = c * (1.0 - lookSchritt) + 0.5 * lookSchritt;
    c = mix(c, texture(look, stelle).rgb, lookStaerke);
  }
  if (vignette > 0.0) {
    // Wie ffmpeg (vf_vignette.c, natural): Abstand zur Mitte durch Abstand
    // zur Ecke, davon cos(Winkel * Abstand)^4. Der Winkel ist pi/5 * (0,5 +
    // Staerke), so schreibt ihn der Export.
    //
    // Gemessen wird am Rahmen, nicht am Bild: der Export passt das Bild erst
    // in die Leinwandform ein (scale+pad) und vignettiert dann. vigSpanne
    // sagt, wie viel von diesem Rahmen die Ebene bedeckt -- 1,0 heisst „bis
    // an den Rand“, 0,3 heisst „ein Streifen in der Mitte, der Rest ist
    // Balken“.
    vec2 d = (abtast - 0.5) * 2.0 * vigSpanne * vec2(seiten, 1.0);
    float dnorm = length(d) / length(vec2(seiten, 1.0));
    float k = dnorm > 1.0 ? 0.0 : cos(0.6283185 * (0.5 + vignette) * dnorm);
    c *= (k * k) * (k * k);
  }
  // Auch die Form und die Vignette werden an der gerasterten Stelle gefragt:
  // ffmpeg rastert das **fertige** Bild, also alles, was die Ebene ausmacht.
  float a = quelle.a * deckung * maskeDeckung(abtast, 0.0);
  // Die Kontur (7.224): zwischen dem Rand der Form und ihrer Stärke nach
  // innen steht ihre Farbe statt des Bildes -- wie im Export, wo das Bild,
  // um die Stärke kleiner geschnitten, auf der Form in Konturfarbe liegt.
  if (maskeArt != 0 && maskeInnen > 0.0) c = mix(maskeKontur, c, maskeDeckung(abtast, maskeInnen));
  if (uebergangArt > 0) {
    // In Leinwandkoordinaten, und y von *oben* -- so laeuft ffmpeg das Bild
    // durch, und an einer Schwarz-Weiss-Blende ist genau das gemessen.
    vec2 stelle = gl_FragCoord.xy / leinwandGroesse;
    float xo = stelle.x;
    float yo = 1.0 - stelle.y;
    float m = 1.0;
    if (uebergangArt == 1) m = xo > 1.0 - uebergangP ? 1.0 : 0.0;
    else if (uebergangArt == 2) m = xo <= uebergangP ? 1.0 : 0.0;
    else if (uebergangArt == 3) m = yo > 1.0 - uebergangP ? 1.0 : 0.0;
    else if (uebergangArt == 4) m = yo <= uebergangP ? 1.0 : 0.0;
    else {
      // Die weichen vier (7.148): dieselbe Kennlinie wie uebergangsMaske im
      // gemeinsamen Teil -- smoothstep, und der Abstand in Ecken gemessen.
      float seiten = leinwandGroesse.x / leinwandGroesse.y;
      vec2 mitte = vec2((xo - 0.5) * seiten, yo - 0.5);
      float eck = length(mitte) / length(vec2(seiten * 0.5, 0.5));
      float t = 0.0;
      if (uebergangArt == 5) t = 2.0 * uebergangP - abs(2.0 * yo - 1.0);
      else if (uebergangArt == 6) t = 2.0 * uebergangP - abs(2.0 * xo - 1.0);
      else if (uebergangArt == 7) t = 3.0 * uebergangP - 0.5 - eck;
      else if (uebergangArt == 8) t = 3.0 * uebergangP - 1.5 + eck;
      // Die Diagonalen messen von ihrer eigenen Ecke, als Produkt der beiden
      // Achsen -- ohne Seitenverhaeltnis, denn sie rechnen in Anteilen.
      else if (uebergangArt == 9) t = 2.0 * uebergangP - 1.0 + xo * yo;
      else if (uebergangArt == 10) t = 2.0 * uebergangP - 1.0 + (1.0 - xo) * (1.0 - yo);
      // Die Uhr: der Winkel von unten gezaehlt, in Bildpunkten gemessen.
      else if (uebergangArt == 11) t = atan((xo - 0.5) * seiten, yo - 0.5) + 7.853982 * (uebergangP - 0.5);
      m = smoothstep(0.0, 1.0, t);
    }
    a *= m;
  }
  if (chromaAn > 0.5) {
    // Dieselben Zeilen wie chromaDeckung im gemeinsamen Teil, und dieselben
    // wie vf_chromakey.c: der Abstand zur Schluesselfarbe in der UV-Ebene,
    // bezogen auf den laengstmoeglichen (Wurzel zwei), und daraus die
    // Deckung ueber die weiche Kante.
    // In acht Bit und gerundet, weil im Film gerundet dasteht (7.145): ein
    // Bild in yuva420p traegt ganze Zahlen, und an der weichen Kante
    // entscheidet genau diese Rundung.
    highp vec3 genau = vec3(roh);
    highp float u = floor(dot(genau, vec3(-0.1482265, -0.2909892, 0.4392157)) * 255.0 + 128.5);
    highp float v = floor(dot(genau, vec3(0.4392157, -0.3677890, -0.0714267)) * 255.0 + 128.5);
    highp float du = u - chromaUV.x;
    highp float dv = v - chromaUV.y;
    highp float abstand = sqrt((du * du + dv * dv) / 2.0) / 255.0;
    a *= chromaWeich <= 0.0001
      ? (abstand > chromaTol ? 1.0 : 0.0)
      : clamp((abstand - chromaTol) / chromaWeich, 0.0, 1.0);
  }
  if (mischart > 0) {
    // **Die Mischung der Spur** (7.132). Gemischt wird mit dem, was schon auf
    // der Leinwand steht: das Bild darunter liegt als Textur bereit, und
    // gerechnet wird wie im Stilblatt (mix-blend-mode) -- erst die Formel,
    // dann mit der Deckung der Ebene darueber.
    vec3 unten = texture(hinten, gl_FragCoord.xy / vec2(textureSize(hinten, 0))).rgb;
    // Die Formeln stehen auf geraden Werten, und gerade liegen sie hier auch
    // vor (7.135) -- die Rueckrechnung, die hier stand, machte eine halb
    // durchsichtige gemischte Ebene um 1/a zu hell.
    vec3 oben = c;
    vec3 gemischt = oben;
    if (mischart == 1) gemischt = unten * oben;
    else if (mischart == 2) gemischt = unten + oben - unten * oben;
    else if (mischart == 3) {
      // soft-light nach der Vorgabe des Stilblatts (W3C), Kanal fuer Kanal.
      vec3 d = vec3(
        unten.r <= 0.25 ? ((16.0 * unten.r - 12.0) * unten.r + 4.0) * unten.r : sqrt(unten.r),
        unten.g <= 0.25 ? ((16.0 * unten.g - 12.0) * unten.g + 4.0) * unten.g : sqrt(unten.g),
        unten.b <= 0.25 ? ((16.0 * unten.b - 12.0) * unten.b + 4.0) * unten.b : sqrt(unten.b)
      );
      vec3 hell = unten + (2.0 * oben - 1.0) * (d - unten);
      vec3 dunkel = unten - (1.0 - 2.0 * oben) * unten * (1.0 - unten);
      gemischt = mix(dunkel, hell, step(vec3(0.5), oben));
    }
    raus = vec4(clamp(gemischt, 0.0, 1.0) * a, a);
    return;
  }
  // Vormultipliziert hinaus: die Leinwand mischt mit ONE / ONE_MINUS_SRC_ALPHA.
  // Vorher stand hier c * deckung -- fuer eine deckende Quelle dasselbe
  // (dort ist a = deckung), fuer einen halb durchsichtigen Punkt zu hell.
  raus = vec4(c * a, a);
}`;

/**
 * Weichzeichnen (7.69g): eine Gauss-Glocke, in zwei Durchgängen -- erst
 * waagerecht, dann senkrecht --, das ist dieselbe Rechnung wie in einem, nur
 * mit 2·n statt n·n Abtastungen. Die Gewichte entstehen im Shader aus dem
 * Sigma; bis 24 Schritte je Seite.
 *
 * Ein grosses Sigma wird nicht mit hunderten Schritten erkauft, sondern mit
 * einem kleineren Bild: der Aufrufer rechnet das Bild vorher so weit
 * herunter, dass das Sigma dort höchstens acht Punkte misst. Das ist die
 * übliche Abkürzung und im Ergebnis vom Vollen nicht zu unterscheiden.
 */
const BLUR_FRAGMENT = `#version 300 es
precision mediump float;
in vec2 vStelle;
uniform sampler2D bild;
uniform vec2 schritt;
uniform float sigma;
out vec4 raus;
void main() {
  float summe = 1.0;
  vec4 c = texture(bild, vStelle);
  for (int i = 1; i <= 24; i++) {
    float x = float(i);
    if (x > sigma * 3.0) break;
    float g = exp(-(x * x) / (2.0 * sigma * sigma));
    c += (texture(bild, vStelle + schritt * x) + texture(bild, vStelle - schritt * x)) * g;
    summe += 2.0 * g;
  }
  raus = c / summe;
}`;

function baue(gl: WebGL2RenderingContext, art: number, quelle: string): WebGLShader {
  const s = gl.createShader(art);
  if (!s) throw new Error('kein Shader');
  gl.shaderSource(s, quelle);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const grund = gl.getShaderInfoLog(s) ?? 'unbekannt';
    gl.deleteShader(s);
    throw new Error(`Shader ging nicht: ${grund}`);
  }
  return s;
}

/**
 * Ob dieses Video-Bild mit texImage2D (neu anlegen) statt texSubImage2D
 * hochgeladen wird. Die ersten sechzig Bilder gehen abwechselnd beide Wege;
 * danach gewinnt der schnellere, und ab und zu (jedes zweihundertste Bild)
 * wird der andere noch einmal probiert, damit eine einmalige Verzögerung
 * nicht für immer entscheidet.
 */
function wegFuerDiesesBild(): boolean {
  const { sub, neu } = messung.probe;
  const n = sub.n + neu.n;
  if (n < 60) return n % 2 === 1;
  if (n % 200 === 0) {
    const mittelSub = sub.ms / Math.max(1, sub.n);
    const mittelNeu = neu.ms / Math.max(1, neu.n);
    messung.weg = mittelNeu < mittelSub ? 'neu' : 'sub';
    return messung.weg === 'sub';
  }
  return messung.weg === 'neu';
}

/** Wie gross eine Quelle ist -- ein Video in Bildpunkten, ein Bild in seinen, eine Fläche in ihren. */
function masseVon(q: TexImageSource): { breite: number; hoehe: number } {
  if (q instanceof HTMLVideoElement) return { breite: q.videoWidth, hoehe: q.videoHeight };
  if (q instanceof HTMLImageElement) return { breite: q.naturalWidth, hoehe: q.naturalHeight };
  if ('width' in q && 'height' in q) return { breite: Number(q.width), hoehe: Number(q.height) };
  return { breite: 0, hoehe: 0 };
}

export class Maler {
  private readonly gl: WebGL2RenderingContext;
  private readonly programm: WebGLProgram;
  private readonly platzPuffer: WebGLBuffer;
  private readonly texturen = new Map<TexImageSource, { textur: WebGLTexture; version: number | undefined; breite: number; hoehe: number }>();
  /** Ein Würfel je Look, einmal gebaut. */
  private readonly looks = new Map<string, WebGLTexture | null>();
  /** Zum Weichzeichnen: ein zweites Programm und zwei Zwischenbilder. */
  private blurProgramm: WebGLProgram | null = null;
  private hintenTextur: WebGLTexture | null = null;
  private hintenMasse = { breite: 0, hoehe: 0 };
  private blurOrte: { schritt: WebGLUniformLocation | null; sigma: WebGLUniformLocation | null } = { schritt: null, sigma: null };
  private zwischen: { fbo: WebGLFramebuffer; textur: WebGLTexture; breite: number; hoehe: number }[] = [];
  private einheitsPuffer: WebGLBuffer | null = null;
  private readonly orte: Record<string, WebGLUniformLocation | null>;

  /**
   * Null, wenn dieses Gerät kein WebGL2 kann. Dann bleibt es beim alten Weg --
   * eine Vorschau, die gar nichts zeigt, wäre schlimmer als eine, die ruckelt.
   */
  static an(leinwand: HTMLCanvasElement): Maler | null {
    const gl = leinwand.getContext('webgl2', {
      // Deckend: die Leinwand *ist* das Bild, schwarz, wo nichts ist. Und sie
      // liegt über den Videoelementen, die darunter sichtbar weiterlaufen --
      // unsichtbare Videos liefern auf iOS kaum Bilder (7.69f).
      alpha: false,
      antialias: false,
      // Das Bild wird nicht zurückgelesen; es steht nur da. Das spart dem
      // Browser eine Kopie je Bild.
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    if (!gl) return null;
    try {
      return new Maler(gl);
    } catch {
      return null;
    }
  }

  private constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    const programm = gl.createProgram();
    if (!programm) throw new Error('kein Programm');
    const v = baue(gl, gl.VERTEX_SHADER, VERTEX);
    const f = baue(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    gl.attachShader(programm, v);
    gl.attachShader(programm, f);
    gl.linkProgram(programm);
    if (!gl.getProgramParameter(programm, gl.LINK_STATUS)) {
      throw new Error(`Programm ging nicht: ${gl.getProgramInfoLog(programm) ?? 'unbekannt'}`);
    }
    gl.deleteShader(v);
    gl.deleteShader(f);
    this.programm = programm;
    gl.useProgram(programm);

    const platz = gl.createBuffer();
    const stelle = gl.createBuffer();
    if (!platz || !stelle) throw new Error('kein Puffer');
    this.platzPuffer = platz;

    const platzOrt = gl.getAttribLocation(programm, 'platz');
    gl.bindBuffer(gl.ARRAY_BUFFER, platz);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(8), gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(platzOrt);
    gl.vertexAttribPointer(platzOrt, 2, gl.FLOAT, false, 0, 0);

    // Die Ecken des Bildes stehen fest: links-oben, rechts-oben, links-unten,
    // rechts-unten -- dieselbe Reihenfolge wie die Ecken aus `ebenenEcken`.
    const stelleOrt = gl.getAttribLocation(programm, 'stelle');
    gl.bindBuffer(gl.ARRAY_BUFFER, stelle);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(stelleOrt);
    gl.vertexAttribPointer(stelleOrt, 2, gl.FLOAT, false, 0, 0);

    this.orte = {
      bild: gl.getUniformLocation(programm, 'bild'),
      look: gl.getUniformLocation(programm, 'look'),
      lookStaerke: gl.getUniformLocation(programm, 'lookStaerke'),
      lookSchritt: gl.getUniformLocation(programm, 'lookSchritt'),
      vignette: gl.getUniformLocation(programm, 'vignette'),
      vigSpanne: gl.getUniformLocation(programm, 'vigSpanne'),
      seiten: gl.getUniformLocation(programm, 'seiten'),
      deckung: gl.getUniformLocation(programm, 'deckung'),
      istFarbe: gl.getUniformLocation(programm, 'istFarbe'),
      farbe: gl.getUniformLocation(programm, 'farbe'),
      werte: gl.getUniformLocation(programm, 'werte'),
      gammaWert: gl.getUniformLocation(programm, 'gammaWert'),
      hinten: gl.getUniformLocation(programm, 'hinten'),
      mischart: gl.getUniformLocation(programm, 'mischart'),
      radTiefen: gl.getUniformLocation(programm, 'radTiefen'),
      radMitten: gl.getUniformLocation(programm, 'radMitten'),
      radHoehen: gl.getUniformLocation(programm, 'radHoehen'),
      radSkala: gl.getUniformLocation(programm, 'radSkala'),
      chromaAn: gl.getUniformLocation(programm, 'chromaAn'),
      chromaUV: gl.getUniformLocation(programm, 'chromaUV'),
      chromaTol: gl.getUniformLocation(programm, 'chromaTol'),
      chromaWeich: gl.getUniformLocation(programm, 'chromaWeich'),
      uebergangArt: gl.getUniformLocation(programm, 'uebergangArt'),
      uebergangP: gl.getUniformLocation(programm, 'uebergangP'),
      leinwandGroesse: gl.getUniformLocation(programm, 'leinwandGroesse'),
      uebergangRaster: gl.getUniformLocation(programm, 'uebergangRaster'),
      maskeArt: gl.getUniformLocation(programm, 'maskeArt'),
      maskeAnzahl: gl.getUniformLocation(programm, 'maskeAnzahl'),
      maskeE: gl.getUniformLocation(programm, 'maskeE'),
      maskeWeich: gl.getUniformLocation(programm, 'maskeWeich'),
      maskeLage: gl.getUniformLocation(programm, 'maskeLage'),
      maskeSeiten: gl.getUniformLocation(programm, 'maskeSeiten'),
      maskeInnen: gl.getUniformLocation(programm, 'maskeInnen'),
      maskeKontur: gl.getUniformLocation(programm, 'maskeKontur'),
    };
    gl.uniform1i(this.orte.bild!, 0);
    gl.uniform1i(this.orte.look!, 1);
    // Einheit 2 gehoert dem Bild darunter (7.132).
    gl.uniform1i(this.orte.hinten!, 2);
    gl.uniform1f(this.orte.lookSchritt!, 1 / LUT_GROESSE);
    // Die Deckung steckt schon in den Farbwerten (siehe Shader): so mischen
    // sich zwei halb durchsichtige Ebenen wie im Export und nicht heller.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    // Nicht umdrehen beim Hochladen. Ein Bild wird von oben nach unten
    // gelesen, eine Textur von unten nach oben -- die beiden gleichen sich
    // aus, weil die erste hochgeladene Zeile (die oberste des Bildes) bei
    // t = 0 landet und die Ecken oben genau dort abtasten. Ein zusätzliches
    // `UNPACK_FLIP_Y_WEBGL` dreht das Bild deshalb auf den Kopf; genau das
    // hat der Durchgang gefunden, und zwar sofort.
  }

  /** Wie viele Punkte die Fläche hat. Ohne das malt die Grafikkarte ins Leere. */
  masse(breite: number, hoehe: number): void {
    const gl = this.gl;
    if (gl.canvas.width !== breite || gl.canvas.height !== hoehe) {
      gl.canvas.width = breite;
      gl.canvas.height = hoehe;
    }
    gl.viewport(0, 0, breite, hoehe);
  }

  /**
   * Das Bild darunter als Textur (7.132).
   *
   * Eine Mischung braucht den Untergrund; auf der Leinwand steht er schon, nur
   * lesen kann der Shader ihn dort nicht. Also einmal herauskopieren, kurz
   * bevor die gemischte Ebene gemalt wird -- dann steht darin genau das, was
   * unter ihr liegt. Ein Kopiervorgang je gemischter Ebene und Bild; die
   * Leinwand hat Vorschaugrösse, das kostet wenig.
   */
  private hintenHolen(): void {
    const gl = this.gl;
    if (!this.hintenTextur) {
      this.hintenTextur = gl.createTexture();
      this.hintenMasse = { breite: 0, hoehe: 0 };
    }
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.hintenTextur);
    const breite = gl.canvas.width;
    const hoehe = gl.canvas.height;
    if (this.hintenMasse.breite !== breite || this.hintenMasse.hoehe !== hoehe) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      // **RGB und nicht RGBA**: die Leinwand ist deckend angelegt
      // (`alpha: false`), ihr Bildspeicher hat also gar keinen Alphakanal --
      // und eine Kopie darf nur verlangen, was dort steht. Mit RGBA gibt
      // WebGL einen Fehler und die Textur bleibt schwarz; genau das stand beim
      // ersten Anlauf im Bild.
      gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGB, 0, 0, breite, hoehe, 0);
      this.hintenMasse = { breite, hoehe };
    } else {
      gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, breite, hoehe);
    }
    // Und die Einheit wieder auf das Bild der Ebene stellen -- alles danach
    // rechnet damit.
    gl.activeTexture(gl.TEXTURE0);
  }

  zeichne(ebenen: Ebene[], filmBreite = 0): void {
    const gl = this.gl;
    messung.gezeichnet += 1;
    const seiten = gl.canvas.width / Math.max(1, gl.canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.programm);
    for (const ebene of ebenen) {
      if (ebene.deckung <= 0) continue;
      const ecken = ebenenEcken({
        kasten: ebene.kasten,
        x: ebene.x,
        y: ebene.y,
        scale: ebene.scale,
        rotate: ebene.rotate,
        leinwand: seiten,
      });
      gl.bindBuffer(gl.ARRAY_BUFFER, this.platzPuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(ecken));
      const werte = ebene.werte ?? [1, 1, 1, 1];
      const mischart = ebene.mischart ?? 0;
      gl.uniform1i(this.orte.mischart!, mischart);
      // Der Untergrund wird geholt, *bevor* diese Ebene darauf kommt.
      if (mischart > 0) this.hintenHolen();
      gl.uniform1f(this.orte.deckung!, ebene.deckung);
      gl.uniform3f(this.orte.werte!, werte[0], werte[1], werte[2]);
      gl.uniform1f(this.orte.gammaWert!, werte[3] ?? 1);
      // Die Farbräder (S14). Ohne sie bleibt die Skala 0, und der Shader
      // überspringt den Block ganz.
      const raeder = ebene.raeder;
      gl.uniform1f(this.orte.radSkala!, raeder ? STUFEN_JE_EINHEIT / 255 : 0);
      if (raeder) {
        const drei = (d: readonly number[]): [number, number, number] => [d[0] ?? 0, d[1] ?? 0, d[2] ?? 0];
        gl.uniform3f(this.orte.radTiefen!, ...drei(raeder.tiefen));
        gl.uniform3f(this.orte.radMitten!, ...drei(raeder.mitten));
        gl.uniform3f(this.orte.radHoehen!, ...drei(raeder.hoehen));
      }
      // Der Übergang als Form (7.147). Ohne ihn bleibt die Art auf 0.
      gl.uniform1i(this.orte.uebergangArt!, ebene.uebergang?.art ?? 0);
      gl.uniform1f(this.orte.uebergangP!, ebene.uebergang?.p ?? 0);
      gl.uniform2f(this.orte.leinwandGroesse!, gl.canvas.width, gl.canvas.height);
      gl.uniform1f(this.orte.uebergangRaster!, ebene.uebergang?.raster ?? 0);
      // Der Greenscreen (S11). Ohne ihn bleibt der Schalter auf 0, und der
      // Shader überspringt den Block.
      const chroma = ebene.chroma;
      gl.uniform1f(this.orte.chromaAn!, chroma ? 1 : 0);
      if (chroma) {
        const [cu, cv] = chromaUV8(rgbVon(chroma.color));
        gl.uniform2f(this.orte.chromaUV!, cu, cv);
        gl.uniform1f(this.orte.chromaTol!, chroma.toleranz);
        gl.uniform1f(this.orte.chromaWeich!, chroma.weich);
      }
      // Der Look als Würfel auf Einheit 1; ohne Look bleibt die Stärke 0 und
      // der Shader lässt ihn liegen.
      const wuerfel = ebene.look && ebene.look.staerke > 0 ? this.look(ebene.look.name) : null;
      gl.uniform1f(this.orte.lookStaerke!, wuerfel ? ebene.look!.staerke : 0);
      if (wuerfel) {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_3D, wuerfel);
      }
      gl.uniform1f(this.orte.vignette!, ebene.vignette ?? 0);
      // **Die Form der Ebene** (7.138). Die Ellipsen kommen aus dem
      // gemeinsamen Teil; ein Kreis bleibt rund, indem die kleinere Seite den
      // Radius vorgibt -- dieselbe Rechnung wie `rundMachen` dort.
      const maske = ebene.maske && ebene.maske.form !== 'keine' ? ebene.maske : null;
      // Nur das Seitenverhaeltnis der Ebene geht ein, nicht ihre Groesse: in
      // der Rechnung kuerzt sie sich weg.
      const mBreite = Math.max(1e-6, ebene.kasten.breite * gl.canvas.width);
      const mHoehe = Math.max(1e-6, ebene.kasten.hoehe * gl.canvas.height);
      gl.uniform1i(this.orte.maskeArt!, maske ? (maske.form === 'ecken' ? 2 : 1) : 0);
      if (maske) {
        gl.uniform1f(this.orte.maskeSeiten!, mBreite / mHoehe);
        gl.uniform1f(this.orte.maskeWeich!, maske.weich);
        const kontur = maske.kontur && maske.kontur.staerke > 0 ? maske.kontur : null;
        gl.uniform1f(this.orte.maskeInnen!, kontur ? kontur.staerke : 0);
        if (kontur) {
          const [r, g, b] = [1, 3, 5].map((i) => parseInt(kontur.farbe.slice(i, i + 2), 16) / 255);
          gl.uniform3f(this.orte.maskeKontur!, r!, g!, b!);
        }
        gl.uniform4f(this.orte.maskeLage!, maske.x, maske.y, maske.groesse, (-maske.drehung * Math.PI) / 180);
        const roh = ellipsen(maske.form);
        // Feste Plätze für höchstens MASKE_ELLIPSEN_MAX Ellipsen -- die Wolke
        // der Gedankenblase hat vierzehn (7.224).
        const werte2 = new Float32Array(4 * MASKE_ELLIPSEN_MAX);
        for (let i = 0; i < Math.min(MASKE_ELLIPSEN_MAX, roh.length); i++) {
          const e = roh[i]!;
          let rx = e.rx;
          let ry = e.ry;
          if (maske.form === 'kreis') {
            const r = Math.min(mBreite / mHoehe, 1) / 2;
            rx = r / (mBreite / mHoehe);
            ry = r;
          }
          // Bläschen und Spitze rechts (7.224): gespiegelt im Feld der Form.
          werte2[i * 4] = maske.gespiegelt ? 1 - e.cx : e.cx;
          werte2[i * 4 + 1] = e.cy;
          werte2[i * 4 + 2] = rx;
          werte2[i * 4 + 3] = ry;
        }
        gl.uniform1i(this.orte.maskeAnzahl!, Math.min(MASKE_ELLIPSEN_MAX, roh.length));
        gl.uniform4fv(this.orte.maskeE!, werte2);
      }
      // Der Rahmen, an dem die Vignette misst (`vignetteRahmen`), hat immer
      // die Form der Leinwand -- also ist sein Seitenverhältnis ihres. Was
      // die Ebene von ihm bedeckt, steht in vigSpanne.
      const rahmen = vignetteRahmen(ebene.kasten);
      gl.uniform1f(this.orte.seiten!, seiten);
      gl.uniform2f(
        this.orte.vigSpanne!,
        ebene.kasten.breite / Math.max(1e-6, rahmen.breite),
        ebene.kasten.hoehe / Math.max(1e-6, rahmen.hoehe),
      );
      if (ebene.quelle) {
        gl.uniform1i(this.orte.istFarbe!, 0);
        gl.activeTexture(gl.TEXTURE0);
        const roh = this.textur(ebene.quelle, ebene.version);
        const masse = masseVon(ebene.quelle);
        // Punkte des Films in Punkte der Quelle: der Kasten ist so breit wie
        // `kasten.breite` Filme, und darin stehen `masse.breite` Punkte.
        const sigma =
          ebene.blur && ebene.blur > 0
            ? filmBreite > 0
              ? (ebene.blur * masse.breite) / (Math.max(1e-6, ebene.kasten.breite) * filmBreite)
              : ebene.blur
            : 0;
        const weich = sigma > 0 ? this.weichgezeichnet(roh, masse, sigma) : null;
        // Weichzeichnen verlässt das Programm; danach muss alles wieder
        // stehen, was `zeichne` vorausgesetzt hat.
        if (weich) {
          gl.useProgram(this.programm);
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
          gl.bindBuffer(gl.ARRAY_BUFFER, this.platzPuffer);
          gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(ecken));
          gl.activeTexture(gl.TEXTURE0);
        }
        gl.bindTexture(gl.TEXTURE_2D, weich ?? roh);
      } else {
        const farbe = ebene.farbe ?? [0, 0, 0];
        gl.uniform1i(this.orte.istFarbe!, 1);
        gl.uniform3f(this.orte.farbe!, farbe[0], farbe[1], farbe[2]);
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
  }

  /**
   * Die Quelle weichgezeichnet, als Textur. Zwei Durchgänge über zwei
   * Zwischenbilder; ein grosses Sigma wird über ein kleineres Bild erreicht
   * (siehe BLUR_FRAGMENT). Das Ergebnis liegt im ersten Zwischenbild.
   */
  private weichgezeichnet(roh: WebGLTexture, masse: { breite: number; hoehe: number }, sigma: number): WebGLTexture | null {
    const gl = this.gl;
    if (!masse.breite || !masse.hoehe) return null;
    if (!this.blurProgramm) {
      try {
        const programm = gl.createProgram();
        if (!programm) return null;
        const v = baue(gl, gl.VERTEX_SHADER, VERTEX);
        const f = baue(gl, gl.FRAGMENT_SHADER, BLUR_FRAGMENT);
        gl.attachShader(programm, v);
        gl.attachShader(programm, f);
        gl.linkProgram(programm);
        if (!gl.getProgramParameter(programm, gl.LINK_STATUS)) return null;
        this.blurProgramm = programm;
        this.blurOrte = { schritt: gl.getUniformLocation(programm, 'schritt'), sigma: gl.getUniformLocation(programm, 'sigma') };
        gl.useProgram(programm);
        gl.uniform1i(gl.getUniformLocation(programm, 'bild'), 0);
        // Ein Rechteck über die ganze Zwischenfläche, links-oben zuerst wie
        // die Ecken sonst auch.
        this.einheitsPuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.einheitsPuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, 1, 1, 1, -1, -1, 1, -1]), gl.STATIC_DRAW);
      } catch {
        return null;
      }
    }
    // Kleiner rechnen, wenn das Sigma gross ist: im Zwischenbild misst es
    // höchstens acht Punkte.
    const faktor = Math.min(1, 8 / sigma);
    const breite = Math.max(2, Math.round(masse.breite * faktor));
    const hoehe = Math.max(2, Math.round(masse.hoehe * faktor));
    const sigmaKlein = sigma * faktor;
    for (let i = 0; i < 2; i++) {
      const z = this.zwischen[i];
      if (z && z.breite === breite && z.hoehe === hoehe) continue;
      if (z) {
        gl.deleteFramebuffer(z.fbo);
        gl.deleteTexture(z.textur);
      }
      const textur = gl.createTexture();
      const fbo = gl.createFramebuffer();
      if (!textur || !fbo) return null;
      gl.bindTexture(gl.TEXTURE_2D, textur);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, breite, hoehe, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, textur, 0);
      this.zwischen[i] = { fbo, textur, breite, hoehe };
    }
    const [a, b] = this.zwischen as [typeof this.zwischen[number], typeof this.zwischen[number]];
    gl.useProgram(this.blurProgramm);
    gl.disable(gl.BLEND);
    gl.viewport(0, 0, breite, hoehe);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.einheitsPuffer);
    const platzOrt = gl.getAttribLocation(this.blurProgramm, 'platz');
    gl.vertexAttribPointer(platzOrt, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1f(this.blurOrte.sigma!, sigmaKlein);
    // Waagerecht: roh -> b (und dabei verkleinert).
    gl.bindFramebuffer(gl.FRAMEBUFFER, b.fbo);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, roh);
    gl.uniform2f(this.blurOrte.schritt!, 1 / breite, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    // Senkrecht: b -> a.
    gl.bindFramebuffer(gl.FRAMEBUFFER, a.fbo);
    gl.bindTexture(gl.TEXTURE_2D, b.textur);
    gl.uniform2f(this.blurOrte.schritt!, 0, 1 / hoehe);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    // Zurück in den Zustand, den `zeichne` erwartet: der Platzpuffer hängt
    // wieder am Attribut, das Mischen ist wieder an.
    gl.bindBuffer(gl.ARRAY_BUFFER, this.platzPuffer);
    gl.vertexAttribPointer(platzOrt, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    return a.textur;
  }

  /**
   * Der Würfel eines Looks als 3D-Textur, einmal gebaut. Dieselbe Rechnung
   * wie die .cube-Datei, die ffmpeg beim Export liest (`looks.ts`) -- bis
   * heute hatte die Vorschau daneben eine Näherung als SVG-Filter.
   */
  private look(name: string): WebGLTexture | null {
    const vorhanden = this.looks.get(name);
    if (vorhanden !== undefined) return vorhanden;
    const gl = this.gl;
    const werte = lutWuerfel(name);
    if (!werte) {
      this.looks.set(name, null);
      return null;
    }
    // Acht Bit je Kanal reichen: ffmpeg rechnet den Export ohnehin in 8 Bit,
    // und Gleitkomma-Texturen darf nicht jede Grafikkarte linear abtasten.
    const bytes = new Uint8Array(werte.length);
    for (let i = 0; i < werte.length; i++) bytes[i] = Math.round(Math.min(1, Math.max(0, werte[i]!)) * 255);
    const textur = gl.createTexture();
    if (!textur) return null;
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_3D, textur);
    // Zeilen aus 17 × 3 Bytes sind nicht durch vier teilbar -- ohne das hier
    // liest die Grafikkarte jede Zeile um ein paar Bytes versetzt.
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB8, LUT_GROESSE, LUT_GROESSE, LUT_GROESSE, 0, gl.RGB, gl.UNSIGNED_BYTE, bytes);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.activeTexture(gl.TEXTURE0);
    this.looks.set(name, textur);
    return textur;
  }

  /**
   * Eine Textur je Quelle, immer wieder überschrieben. Für jedes Bild eine
   * neue anzulegen wäre der sichere Weg in den vollen Speicher.
   */
  private textur(quelle: TexImageSource, version: number | undefined): WebGLTexture {
    const gl = this.gl;
    let stand = this.texturen.get(quelle);
    if (!stand) {
      const neu = gl.createTexture();
      if (!neu) throw new Error('keine Textur');
      stand = { textur: neu, version: undefined, breite: 0, hoehe: 0 };
      this.texturen.set(quelle, stand);
      gl.bindTexture(gl.TEXTURE_2D, neu);
      // Kein Wiederholen: sonst zieht der Rand eines Bildes am anderen Ende
      // wieder herein, sobald es gedreht ist.
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    } else {
      gl.bindTexture(gl.TEXTURE_2D, stand.textur);
      // Ein Bild, das sich nicht geändert hat, wird nicht noch einmal
      // hochgeladen: ein gesetzter Text in Leinwandgrösse sechzigmal je
      // Sekunde wäre die teuerste Art, nichts zu tun.
      if (version !== undefined && stand.version === version) return stand.textur;
    }
    try {
      const { breite, hoehe } = masseVon(quelle);
      const start = performance.now();
      const istVideo = quelle instanceof HTMLVideoElement;
      // Welcher Weg? Auf dem Rechner ist texSubImage2D (in den vorhandenen
      // Speicher) billiger. Auf iOS ist es womöglich umgekehrt: dort hat
      // texImage2D aus einem Video einen schnellen Pfad, texSubImage2D nicht
      // unbedingt. Also werden beide Wege eine Weile gemessen, und der
      // schnellere bleibt. Die Zahlen stehen im Bericht.
      const neuAnlegen = breite !== stand.breite || hoehe !== stand.hoehe || !breite || (istVideo && wegFuerDiesesBild());
      // **Vormultipliziert hochladen** (7.135). Die Voreinstellung ist
      // `false`, und damit kam eine Leinwand mit geraden Werten herein --
      // gemessen am 21. September: ein Textkasten mit boxOpacity 0,6 auf
      // rgb(30,122,60) ergab rgb(45,134,227) statt rgb(32,100,146), also ein
      // Kasten, der fast deckend aussah.
      //
      // Vormultipliziert, weil zwei Stellen es so brauchen: das
      // Weichzeichnen mittelt ueber Nachbarn, und wer dabei gerade Werte
      // mittelt, mischt die Farbe durchsichtiger Nachbarn (also Schwarz) mit
      // und bekommt einen dunklen Saum; die Leinwand mischt ebenso
      // (ONE / ONE_MINUS_SRC_ALPHA). Die Farbrechnungen wollen es
      // andersherum, und die rechnen im Shader zurueck.
      //
      // Danach wieder aus: der Wuerfel des Looks kommt als Zahlenfeld herein,
      // und dabei darf der Schalter nicht an sein.
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      if (neuAnlegen) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, quelle);
        stand.breite = breite;
        stand.hoehe = hoehe;
      } else {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, quelle);
      }
      stand.version = version;
      if (istVideo) {
        const dauer = performance.now() - start;
        messung.hochgeladen += 1;
        messung.ms += dauer;
        if (dauer > messung.msMax) messung.msMax = dauer;
        const p = neuAnlegen ? messung.probe.neu : messung.probe.sub;
        p.n += 1;
        p.ms += dauer;
      }
    } catch {
      // Ein Video, das noch kein Bild hat, wirft. Dann bleibt die Textur, wie
      // sie war -- das letzte Bild ist besser als ein schwarzes.
    } finally {
      // Auch wenn es geworfen hat: der Schalter muss wieder aus. Bleibt er an,
      // faellt das Hochladen des Look-Wuerfels aus -- ein Zahlenfeld duldet
      // ihn nicht, und dann steht der Wuerfel schwarz da.
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    }
    return stand.textur;
  }

  /**
   * Was an einer Stelle steht, 0..255 je Kanal.
   *
   * Nur zum Nachmessen -- der Durchgang liest damit nach, dass wirklich etwas
   * gemalt wurde und nicht nur eine leere Fläche dasteht. Gültig ist das
   * Ergebnis unmittelbar nach `zeichne()`: die Zeichenfläche wird nicht
   * aufbewahrt (`preserveDrawingBuffer: false`), weil das den Browser bei
   * jedem Bild eine Kopie kosten würde.
   *
   * `x` und `y` sind Anteile, 0..1, gezählt wie im Bild: oben links ist 0,0.
   */
  /**
   * Das **ganze** Bild der Leinwand, als PNG-Adresse (7.136).
   *
   * Wie `punkt`, nur nicht ein Punkt, sondern alles -- und aus demselben
   * Grund: nur so lässt sich nachsehen, ob die Vorschau malt, was der Film
   * zeigt. Ein Abbild vom Schirm wäre etwas anderes: darin steckt der Rand des
   * Elements, eine Meldung, die darüber liegt, und die Umrechnung auf die
   * Punkte des Telefons. Hier kommt der Bildspeicher selbst.
   *
   * Gültig ist das Ergebnis unmittelbar nach `zeichne()` -- die Zeichenfläche
   * wird nicht aufbewahrt (`preserveDrawingBuffer: false`).
   */
  bild(): string {
    const gl = this.gl;
    const b = gl.canvas.width;
    const h = gl.canvas.height;
    const px = new Uint8Array(b * h * 4);
    gl.readPixels(0, 0, b, h, gl.RGBA, gl.UNSIGNED_BYTE, px);
    const flaeche = document.createElement('canvas');
    flaeche.width = b;
    flaeche.height = h;
    const ctx = flaeche.getContext('2d');
    if (!ctx) return '';
    const bild = ctx.createImageData(b, h);
    // `readPixels` liest von unten nach oben, ein Bild geht von oben nach
    // unten -- also Zeile fuer Zeile umgedreht. Und das Alpha auf deckend:
    // die Leinwand ist ohne Alphakanal angelegt, was dort steht, ist Zufall.
    for (let y = 0; y < h; y++) {
      const von = (h - 1 - y) * b * 4;
      const nach = y * b * 4;
      bild.data.set(px.subarray(von, von + b * 4), nach);
      for (let x = 0; x < b; x++) bild.data[nach + x * 4 + 3] = 255;
    }
    ctx.putImageData(bild, 0, 0);
    return flaeche.toDataURL('image/png');
  }

  punkt(x: number, y: number): [number, number, number, number] {
    const gl = this.gl;
    const px = new Uint8Array(4);
    const sx = Math.min(gl.canvas.width - 1, Math.max(0, Math.round(x * gl.canvas.width)));
    const sy = Math.min(gl.canvas.height - 1, Math.max(0, Math.round((1 - y) * gl.canvas.height)));
    gl.readPixels(sx, sy, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    return [px[0]!, px[1]!, px[2]!, px[3]!];
  }

  /** Beim Aufräumen: die Grafikkarte gibt von selbst nichts zurück. */
  frei(): void {
    const gl = this.gl;
    for (const t of this.texturen.values()) gl.deleteTexture(t.textur);
    this.texturen.clear();
    for (const t of this.looks.values()) if (t) gl.deleteTexture(t);
    this.looks.clear();
    for (const z of this.zwischen) {
      gl.deleteFramebuffer(z.fbo);
      gl.deleteTexture(z.textur);
    }
    this.zwischen = [];
    if (this.blurProgramm) gl.deleteProgram(this.blurProgramm);
    if (this.einheitsPuffer) gl.deleteBuffer(this.einheitsPuffer);
    gl.deleteBuffer(this.platzPuffer);
    gl.deleteProgram(this.programm);
  }
}
